// src/components/services/LifeInsurance.jsx
import React from "react";
import { motion } from "framer-motion";
import { Link } from 'react-router-dom'; // Import Link
import {
    MdOutlineSecurity,
    MdOutlineDescription,
    MdOutlineVerifiedUser,
    MdOutlinePayment,
} from "react-icons/md";
import { FaPhoneAlt, FaShieldAlt, FaPiggyBank, FaUsers, FaUserTie, } from "react-icons/fa";


function LifeInsurance() {
    const serviceName = "Life Insurance"; // Set the service name

    // Animation Variants (Keep, or move to utils/animations.js)
    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.2, // Stagger animation of child elements
            },
        },
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 30 },
        visible: { opacity: 1, y: 0 },
    };

    return (
        <section className="py-16 md:py-24 px-4 md:px-8 bg-gray-50 text-gray-700">
            <div className="container mx-auto">
                <motion.div
                    initial="hidden"
                    animate="visible"
                    variants={containerVariants}
                >
                    <motion.h1
                        variants={itemVariants}
                        className="text-4xl md:text-5xl font-bold text-gray-800 mb-6 md:mb-8 text-center"
                    >
                        Life Insurance
                    </motion.h1>
                    <motion.p
                        variants={itemVariants}
                        className="text-lg md:text-xl text-gray-600 mb-10 md:mb-12 text-center max-w-3xl mx-auto"
                    >
                        Secure your family's financial future with a life insurance plan from Keziah Global Private Limited, in partnership with Fino Payments Bank. Get the peace of mind you deserve, knowing your loved ones will be protected.
                    </motion.p>

                    {/* Features and Benefits */}
                    <motion.div
                        variants={itemVariants}
                        className="mb-12 md:mb-16 bg-white rounded-xl shadow-md p-6 md:p-10"
                    >
                        <h3 className="text-2xl md:text-3xl font-bold text-gray-800 mb-6 text-center">
                            Key Features
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                            {/* Feature 1 */}
                            <motion.div
                                className="flex items-start space-x-4"
                                whileHover={{ scale: 1.05 }}
                                transition={{ duration: 0.2 }}
                            >
                                <div className="p-3 rounded-full bg-blue-100">
                                    {/* Replaced Icon */}
                                    <FaUsers className="text-blue-600 text-2xl" />
                                </div>
                                <div>
                                    <h4 className="font-bold text-gray-800 text-lg">
                                        Financial Protection for Your Family
                                    </h4>
                                    <p className="text-gray-600">
                                        Ensure your family's financial well-being in case of an unfortunate event.
                                    </p>
                                </div>
                            </motion.div>
                            {/* Feature 2 */}
                            <motion.div
                                className="flex items-start space-x-4"
                                whileHover={{ scale: 1.05 }}
                                transition={{ duration: 0.2, delay: 0.1 }}
                            >
                                <div className="p-3 rounded-full bg-blue-100">
                                    <FaShieldAlt className="text-blue-600 text-2xl" />
                                </div>
                                <div>
                                    <h4 className="font-bold text-gray-800 text-lg">
                                        Coverage for Death and Disability
                                    </h4>
                                    <p className="text-gray-600">
                                        Get coverage against death, disability, and critical illnesses (depending on the plan).
                                    </p>
                                </div>
                            </motion.div>
                            {/* Feature 3 */}
                            <motion.div
                                className="flex items-start space-x-4"
                                whileHover={{ scale: 1.05 }}
                                transition={{ duration: 0.2, delay: 0.2 }}
                            >
                                <div className="p-3 rounded-full bg-blue-100">
                                    <FaPiggyBank className="text-blue-600 text-2xl" />
                                </div>
                                <div>
                                    <h4 className="font-bold text-gray-800 text-lg">
                                        Savings and Investment Options
                                    </h4>
                                    <p className="text-gray-600">
                                        Some plans offer savings and investment components to help you grow your wealth.
                                    </p>
                                </div>
                            </motion.div>
                            {/* Feature 4 */}
                            <motion.div
                                className="flex items-start space-x-4"
                                whileHover={{ scale: 1.05 }}
                                transition={{ duration: 0.2, delay: 0.3 }}
                            >
                                <div className="p-3 rounded-full bg-blue-100">
                                    <MdOutlinePayment className="text-blue-600 text-2xl" />
                                </div>
                                <div>
                                    <h4 className="font-bold text-gray-800 text-lg">
                                        Flexible Premium Payment
                                    </h4>
                                    <p className="text-gray-600">
                                        Choose a premium payment term that suits your financial needs and preferences.
                                    </p>
                                </div>
                            </motion.div>
                            {/* Feature 5 */}
                            <motion.div
                                className="flex items-start space-x-4"
                                whileHover={{ scale: 1.05 }}
                                transition={{ duration: 0.2, delay: 0.4 }}
                            >
                                <div className="p-3 rounded-full bg-blue-100">
                                    <MdOutlineVerifiedUser className="text-blue-600 text-2xl" />
                                </div>
                                <div>
                                    <h4 className="font-bold text-gray-800 text-lg">
                                        Easy Claim Process
                                    </h4>
                                    <p className="text-gray-600">
                                        Experience a hassle-free claim process to support your family during difficult times.
                                    </p>
                                </div>
                            </motion.div>
                            {/* Feature 6 */}
                            <motion.div
                                className="flex items-start space-x-4"
                                whileHover={{ scale: 1.05 }}
                                transition={{ duration: 0.2, delay: 0.5 }}
                            >
                                <div className="p-3 rounded-full bg-blue-100">
                                    {/* Replaced Icon */}
                                    <FaUserTie className="text-blue-600 text-2xl" />
                                </div>
                                <div>
                                    <h4 className="font-bold text-gray-800 text-lg">
                                        Expert Guidance
                                    </h4>
                                    <p className="text-gray-600">
                                        Our team will help you choose the right life insurance plan to meet your specific needs.
                                    </p>
                                </div>
                            </motion.div>
                        </div>
                    </motion.div>

                    {/* Why Choose Keziah for Life Insurance */}
                    <motion.div
                        variants={itemVariants}
                        className="mb-12 md:mb-16 bg-white rounded-xl shadow-md p-6 md:p-10"
                    >
                        <h3 className="text-2xl md:text-3xl font-bold text-gray-800 mb-6 text-center">
                            Why Choose Keziah for Life Insurance
                        </h3>
                        <ul className="list-disc pl-8 space-y-4 text-gray-600 text-lg">
                            <li>Wide range of plans to suit different needs and budgets.</li>
                            <li>Trusted partnership with Fino Payments Bank.</li>
                            <li>Simple and transparent application process.</li>
                            <li>Dedicated customer support to assist you every step of the way.</li>

                        </ul>
                    </motion.div>

                    {/* Call to Action */}
                    <motion.div
                        variants={itemVariants}
                        className="mb-12 md:mb-16 bg-white rounded-xl shadow-md p-6 md:p-10"
                    >
                        <h3 className="text-2xl md:text-3xl font-bold text-gray-800 mb-6 text-center">
                            Get Your Life Insurance Quote Today!
                        </h3>
                        <p className="text-gray-600 text-lg text-center">
                            Visit our branch in Salem, Tamil Nadu, to discuss your life insurance needs with our experts. You can also request a callback to get personalized assistance and find the perfect plan for you.
                        </p>
                        <motion.div
                            variants={itemVariants}
                            className="mt-10 flex flex-col md:flex-row justify-center space-y-4 md:space-y-0 md:space-x-6"
                        >
                            <a
                                href="tel:9043588446"
                                className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-6 rounded-full text-base md:text-lg transition duration-300 ease-in-out shadow-md hover:shadow-lg text-center w-full md:w-auto flex items-center justify-center"
                                whileHover={{ scale: 1.05 }}
                            >
                                <FaPhoneAlt className="inline-block mr-2" />
                                Call Now
                            </a>
                            {/* Use Link for Callback */}
                            <Link to={`/callback?serviceName=${encodeURIComponent(serviceName)}`}>
                                <motion.button
                                    className="bg-gray-800 hover:bg-gray-700 text-white font-bold py-3 px-6 rounded-full text-base md:text-lg transition duration-300 ease-in-out shadow-md hover:shadow-lg text-center w-full md:w-auto flex items-center justify-center"
                                    whileHover={{ scale: 1.05 }}
                                >
                                    Request a Callback
                                </motion.button>
                            </Link>
                        </motion.div>
                    </motion.div>
                </motion.div>
            </div>
        </section>
    );
}

export default LifeInsurance;